import { useCallback, useState } from 'react';

import { useEmployerReactivationStore } from '@/modules/employer/components/EmployerReactivation/stores/useEmployerReactivation.store';
import { useEmployerReactivationModalStore } from '@/modules/employer/components/EmployerReactivation/stores';
import { ReactivationSteps } from '@/modules/employer/components/EmployerReactivation/types';
import { useReactivateEmployerQuery } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationModalForms/queries/useReactivateEmployer.query';
import { useEmployerReactivationValidation } from '@/modules/employer/components/EmployerReactivation/hooks/useEmployerReactivationValidation';

interface Props {
  onCancel?: () => void;
}

export const useReactivateEmployerModal = ({ onCancel }: Props) => {
  const [isServerError, setIsServerError] = useState(false);
  const { errors, validate, hasFieldErrors } = useEmployerReactivationValidation();
  
  const payrollValue = useEmployerReactivationStore((state) => state.payrollValue);
  const fundingValue = useEmployerReactivationStore((state) => state.fundingValue);
  const reCardEmployees = useEmployerReactivationStore((state) => state.reCardEmployees);
  const reactivatePlans = useEmployerReactivationStore((state) => state.reactivatePlans);
  const selectedPlans = useEmployerReactivationStore((state) => state.selectedPlans);
  
  const handleSetStep = useEmployerReactivationModalStore((state) => state.handleSetStep);
  const employerId = useEmployerReactivationModalStore((state) => state.employerId);
  
  const { reactivateEmployer, isLoading } = useReactivateEmployerQuery(employerId || '');

  const handleCancel = useCallback(() => {
    onCancel?.();
  }, [onCancel]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    
    const payload = {
      payroll_contribution_account_id: Number(payrollValue),
      funding_replenishment_account_id: Number(fundingValue),
      is_re_card_needed: reCardEmployees,
      plans_to_reactivate: reactivatePlans 
        ? selectedPlans.map(({ plan_id, external_bank_account_id, isPlanLevelFunding }) => {
            const plan: { plan_id: number; external_bank_account_id?: number } = { plan_id };
            if (isPlanLevelFunding) {
              plan.external_bank_account_id = external_bank_account_id;
            }
            return plan;
          })
        : [],
    };
    
    try {
      await reactivateEmployer(payload);
      handleSetStep(ReactivationSteps.RESUME);
    } catch {
      setIsServerError(true);
    }
  }, [validate, payrollValue, fundingValue, reCardEmployees, reactivatePlans, selectedPlans, reactivateEmployer, handleSetStep]);

  return {
    errors,
    hasFieldErrors,
    isLoading,
    isServerError,
    setIsServerError,
    handleCancel,
    handleSubmit,
  };
};
